import { useMemo } from "react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { formatNumber } from "~/utils/formatting"
import { DateFormatType, formatDate, utcMondayMs } from "~/utils/datetime"
import { formatName } from "~/utils/namingUtils"
import SuperChart, { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { ChartType, DateType } from "~/constants/graphs"

interface Props {
  entries: { skuId: string; warehouseId: string }[]
  preordersMapByDay: Map<string, Map<string, { date: Date; quantity: number }[]>> | undefined
  expectedArrivalDate: Date
  nextOrderArrivalDate: Date
  total: number
  children: React.ReactNode
}

export function PreordersInPeriodHoverCard({
  entries,
  preordersMapByDay,
  expectedArrivalDate,
  nextOrderArrivalDate,
  total,
  children,
}: Props) {
  const series = useMemo((): DateChartSeries[] => {
    if (!preordersMapByDay) return []

    const weeklyData = new Map<number, number>()
    for (const entry of entries) {
      const skuWhMap = preordersMapByDay.get(entry.skuId)
      if (!skuWhMap) continue
      for (const [, dayData] of skuWhMap) {
        for (const { date, quantity } of dayData) {
          const weekMs = utcMondayMs(date)
          weeklyData.set(weekMs, (weeklyData.get(weekMs) ?? 0) + quantity)
        }
      }
    }

    if (!weeklyData.size) return []

    const data = Array.from(weeklyData.entries())
      .sort(([a], [b]) => a - b)
      .map(([ms, value]) => ({ milliseconds: ms, value }))

    return [
      {
        name: formatName("preorder", true, true),
        data,
        type: "column",
        colors: ["rgb(169, 139, 250)"],
      },
    ]
  }, [entries, preordersMapByDay])

  return (
    <HoverCard>
      <HoverCardTrigger className="flex h-full w-full items-center">{children}</HoverCardTrigger>
      <HoverCardContent className="w-80 p-0" align="start">
        <div className="px-4 py-3 border-b border-base bg-base">
          <span className="label-base text-fg-base">
            {formatName("preorder", true, true)} in period
          </span>
          <div className="text-2xl font-semibold mt-1 text-fg-base">
            {formatNumber(total)} units
          </div>
          <p className="paragraph-sm text-fg-subtle mt-1">
            {formatDate(expectedArrivalDate, DateFormatType.DayMonth)} –{" "}
            {formatDate(nextOrderArrivalDate, DateFormatType.DayMonth)}
          </p>
        </div>
        <div className="px-2 py-2">
          {series.length > 0 ? (
            <SuperChart
              type={ChartType.TimeSeries}
              dateType={DateType.Weekly}
              data={series}
              height={160}
              noLegend={true}
              margin={[5, 5, 30, 40]}
            />
          ) : (
            <div className="flex h-40 items-center justify-center paragraph-sm text-fg-subtle">
              No {formatName("preorder", true)} in this period
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
